import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Download, LogOut, Search, Trash2, RefreshCw, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toCsv = (rows) => {
  const header = ["id", "created_at", "full_name", "phone", "email", "locations", "message"];
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const body = rows.map((r) =>
    [
      r.id,
      r.created_at,
      r.full_name,
      r.phone,
      r.email,
      (r.locations || []).join("; "),
      r.message || "",
    ]
      .map(escape)
      .join(",")
  );
  return [header.join(","), ...body].join("\n");
};

export default function AdminDashboard() {
  const { user, loading, logout, authAxios } = useAuth();
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [fetching, setFetching] = useState(true);
  const [activeLead, setActiveLead] = useState(null);

  const refresh = async () => {
    setFetching(true);
    try {
      const client = authAxios();
      const [leadsRes, statsRes] = await Promise.all([
        client.get("/admin/leads"),
        client.get("/admin/stats"),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        nav("/admin/login", { replace: true });
      } else {
        toast.error("Could not load leads.");
      }
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (user) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const filtered = useMemo(() => {
    if (!q.trim()) return leads;
    const s = q.toLowerCase();
    return leads.filter((l) =>
      [l.full_name, l.email, l.phone, l.message, (l.locations || []).join(" ")]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [q, leads]);

  const exportCsv = () => {
    if (!filtered.length) {
      toast.error("Nothing to export yet.");
      return;
    }
    const blob = new Blob([toCsv(filtered)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `aio-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const remove = async (lead) => {
    if (!window.confirm(`Delete request from ${lead.full_name}?`)) return;
    try {
      await authAxios().delete(`/admin/leads/${lead.id}`);
      setLeads((prev) => prev.filter((l) => l.id !== lead.id));
      if (activeLead?.id === lead.id) setActiveLead(null);
      toast.success("Deleted.");
    } catch {
      toast.error("Could not delete.");
    }
  };

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-aio-bg" data-testid="admin-dashboard">
      <header className="border-b border-aio-border/60 bg-aio-bg sticky top-0 z-40">
        <div className="max-w-[1440px] mx-auto px-6 sm:px-12 lg:px-20 h-16 flex items-center gap-6">
          <a href="/" className="flex items-baseline gap-3">
            <span className="font-display text-2xl tracking-tighter text-aio-ink">A.I.O</span>
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.28em] text-aio-soft">
              Sourcing Console
            </span>
          </a>
          <span className="ml-auto text-xs uppercase tracking-[0.22em] text-aio-soft hidden sm:inline">
            {user.email}
          </span>
          <button
            onClick={refresh}
            data-testid="admin-refresh-button"
            className="p-2 hover:bg-aio-surface transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${fetching ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={() => {
              logout();
              nav("/admin/login", { replace: true });
            }}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-aio-soft hover:text-aio-ink"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-6 sm:px-12 lg:px-20 py-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12 items-end">
          <div className="md:col-span-6">
            <p className="text-[11px] uppercase tracking-[0.32em] text-aio-soft mb-4">
              Incoming requests
            </p>
            <h1 className="font-display text-5xl sm:text-6xl leading-[1.02] tracking-tighter text-aio-ink">
              Brochure requests.
            </h1>
          </div>
          <div className="md:col-span-6 grid grid-cols-2 gap-6">
            <div className="border-l border-aio-border/70 pl-5">
              <div className="font-display text-4xl text-aio-ink" data-testid="stat-total">
                {stats?.total_leads ?? "\u2014"}
              </div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-aio-soft mt-1">
                Total requests
              </p>
            </div>
            <div className="border-l border-aio-border/70 pl-5">
              <div className="font-display text-4xl text-aio-ink" data-testid="stat-week">
                {stats?.leads_last_7_days ?? "\u2014"}
              </div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-aio-soft mt-1">
                Last 7 days
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-aio-soft pointer-events-none" />
            <input
              data-testid="admin-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email, phone, location…"
              className="editorial-input pl-7"
            />
          </div>
          <button
            onClick={exportCsv}
            data-testid="admin-export-csv"
            className="inline-flex items-center gap-2 px-5 py-3 border border-aio-border text-aio-ink hover:bg-aio-surface transition-colors text-xs uppercase tracking-[0.22em]"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        {fetching && leads.length === 0 ? (
          <div className="py-24 flex items-center justify-center text-aio-soft">
            <Loader2 className="w-5 h-5 animate-spin mr-3" />
            Loading requests...
          </div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="admin-empty-state"
            className="border border-aio-border/60 bg-aio-surface p-16 text-center"
          >
            <p className="font-display text-3xl italic text-aio-ink mb-3">No requests yet.</p>
            <p className="text-aio-soft text-sm">
              When someone submits the brochure form, it will appear here.
            </p>
          </div>
        ) : (
          <div
            data-testid="admin-leads-table"
            className="border border-aio-border/60 bg-aio-surface overflow-hidden"
          >
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-aio-border/60 text-[10px] uppercase tracking-[0.22em] text-aio-soft">
              <div className="col-span-3">Name</div>
              <div className="col-span-3">Email</div>
              <div className="col-span-2">Phone</div>
              <div className="col-span-3">Locations</div>
              <div className="col-span-1 text-right">Date</div>
            </div>
            <ul>
              {filtered.map((l, idx) => (
                <motion.li
                  key={l.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: Math.min(idx * 0.02, 0.3) }}
                  className="border-b border-aio-border/50 last:border-b-0"
                  data-testid={`lead-row-${l.id}`}
                >
                  <button
                    onClick={() => setActiveLead(l)}
                    className="w-full grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-5 text-left hover:bg-aio-bg transition-colors"
                  >
                    <div className="md:col-span-3">
                      <div className="font-display text-xl text-aio-ink">{l.full_name}</div>
                      {l.message && (
                        <div className="text-xs text-aio-soft mt-1 line-clamp-1">{l.message}</div>
                      )}
                    </div>
                    <div className="md:col-span-3 text-sm text-aio-ink truncate">{l.email}</div>
                    <div className="md:col-span-2 text-sm text-aio-soft">{l.phone}</div>
                    <div className="md:col-span-3 flex flex-wrap gap-1.5">
                      {(l.locations || []).slice(0, 3).map((loc) => (
                        <span
                          key={loc}
                          className="text-[10px] uppercase tracking-[0.18em] border border-aio-border px-2 py-0.5 text-aio-soft"
                        >
                          {loc}
                        </span>
                      ))}
                      {(l.locations || []).length > 3 && (
                        <span className="text-[10px] text-aio-soft">
                          +{l.locations.length - 3}
                        </span>
                      )}
                    </div>
                    <div className="md:col-span-1 text-xs text-aio-soft md:text-right">
                      {formatDate(l.created_at)}
                    </div>
                  </button>
                </motion.li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {activeLead && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4"
          onClick={() => setActiveLead(null)}
          data-testid="lead-detail-drawer"
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl bg-aio-bg border border-aio-border/60 p-8 sm:p-12"
          >
            <p className="text-[11px] uppercase tracking-[0.32em] text-aio-soft mb-4">
              Request detail
            </p>
            <h2 className="font-display text-4xl text-aio-ink mb-2">{activeLead.full_name}</h2>
            <p className="text-aio-soft mb-8">{formatDate(activeLead.created_at)}</p>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
              <div>
                <dt className="text-[10px] uppercase tracking-[0.28em] text-aio-soft mb-1">
                  Email
                </dt>
                <dd className="text-aio-ink">
                  <a href={`mailto:${activeLead.email}`} className="hover:underline">
                    {activeLead.email}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-[0.28em] text-aio-soft mb-1">
                  Phone
                </dt>
                <dd className="text-aio-ink">
                  <a href={`tel:${activeLead.phone}`} className="hover:underline">
                    {activeLead.phone}
                  </a>
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-[10px] uppercase tracking-[0.28em] text-aio-soft mb-1">
                  Preferred locations
                </dt>
                <dd className="flex flex-wrap gap-2 mt-1">
                  {(activeLead.locations || []).length ? (
                    activeLead.locations.map((loc) => (
                      <span
                        key={loc}
                        className="text-xs uppercase tracking-[0.18em] border border-aio-border px-3 py-1 text-aio-ink"
                      >
                        {loc}
                      </span>
                    ))
                  ) : (
                    <span className="text-aio-soft text-sm">None specified</span>
                  )}
                </dd>
              </div>
              {activeLead.message && (
                <div className="sm:col-span-2">
                  <dt className="text-[10px] uppercase tracking-[0.28em] text-aio-soft mb-1">
                    Message
                  </dt>
                  <dd className="text-aio-ink text-base leading-relaxed">{activeLead.message}</dd>
                </div>
              )}
            </dl>

            <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-aio-border/60">
              <a
                href={`mailto:${activeLead.email}?subject=Your%20A.I.O%20Luxury%20Brochure&body=Hi%20${encodeURIComponent(
                  activeLead.full_name.split(" ")[0] || ""
                )},%0D%0A%0D%0AThank%20you%20for%20requesting%20our%20brochure.`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-aio-olive text-aio-bg hover:bg-aio-oliveDeep transition-colors text-xs uppercase tracking-[0.22em]"
                data-testid="lead-reply-button"
              >
                Reply via email
              </a>
              <button
                onClick={() => remove(activeLead)}
                data-testid="lead-delete-button"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-red-700 hover:text-red-900"
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
              <button
                onClick={() => setActiveLead(null)}
                data-testid="lead-close-button"
                className="ml-auto text-xs uppercase tracking-[0.22em] text-aio-soft hover:text-aio-ink"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
